import { GET_FANTASY_ARTICLE_BY_ID } from '@/graphql/fantsy-tips/fantasyTips.query'
import {
  FULL_SCOREBOARD,
  GET_COMMENTARY,
  GET_FANTASY_OVERVIEW,
  GET_FEATURED_SESSION_STATS,
  GET_HEAD_TO_HEAD,
  GET_MATCH_ODDS,
  GET_MATCH_OVERVIEW,
  GET_MATCH_PLAYING_XI,
  GET_MATCH_SQUAD,
  GET_MATCH_STATS,
  GET_MATCHES_DATA,
  GET_NEWS_DATA_BY_MATCH,
  GET_RECENT_MATCHES_STATS,
  GET_VENUE_STATS,
  LIVE_INNING_DATA,
  MATCH_DETAIL,
  RECENT_OVER
} from '@/graphql/match/match.query'
import { GET_SUB_PAGE_BY_SLUG_RAW } from '@/graphql/seo/seo.query'
import { GET_ROUNDS, GET_STANDING_DATA } from '@/graphql/series/standings.query'
import queryGraphql from '@/shared/components/queryGraphql'
import { ENUM_CRICKET_MATCH_PAGES } from '@/shared/enum'
import { seoDataType } from '@/shared/hooks/useSeoData'
// import getSeoData from '@/shared/hooks/useSeoData'
import { combineQuery, getSlugUrl, parallelCalls } from '@/shared/utils'
import { unstable_cache } from 'next/cache'
import {
  CommonInputType,
  GetFeaturedSessionsByMatchIdResponse,
  GetHeadToHeadResponse,
  GetLiveInningsResponse,
  GetLiveStatsDataResponse,
  GetMatchDetailResponse,
  GetMatchOverviewFrontResponse,
  GetMatchSquadPlayerResponse,
  GetMatchSubPagesResponse,
  GetNewsByMatchIdResponse,
  GetOddsHistoryByMatchIdResponse,
  GetRecentStatsOfTeamResponse,
  GetScorecardDataResponse,
  GetVenueStatsByMatchIdResponse,
  MatchDynamicTabData,
  MatchLiveSummaryData,
  MatchOverviewData,
  MatchOverviewFrontResponse,
  UpcomingMatchCard
} from './types'

function tabNameConversion(subType: string) {
  switch (subType) {
    case 'ls':
      return 'live-stats'
    case 'l':
      return 'live'
  }
}

function getNavItems(
  dynamicTabs: GetMatchSubPagesResponse['data']['getSeosBySlug'],
  currentTab: string,
  matchStatus: string
) {
  const items: { navItem: string; url: string; active: boolean }[] = []
  dynamicTabs?.forEach(t => {
    const tabName = t?.eSubType ? tabNameConversion(t?.eSubType) : ''
    const navItem =
      tabName && matchStatus === 'live' ? tabName : ENUM_CRICKET_MATCH_PAGES[t?.eSubType as string]
    if (t?.eSubType && navItem) {
      items.push({
        navItem: navItem === 'fixtures-and-results' ? 'fixtures' : navItem.replace('-', ' '),
        url: getSlugUrl(`/${t?.sSlug}`),
        active:
          t?.eSubType === 'cm'
            ? ENUM_CRICKET_MATCH_PAGES.c === currentTab
            : ENUM_CRICKET_MATCH_PAGES[t?.eSubType as string] === currentTab
      })
    } else if (t?.eSubType === null) {
      // Home Tab and live tab
      items.push({
        navItem: ENUM_CRICKET_MATCH_PAGES.i,
        url: getSlugUrl(`/${t?.sSlug}`),
        active: ENUM_CRICKET_MATCH_PAGES.i === currentTab
      })
    }
  })
  return items
}

export async function getMatchData(seoData: seoDataType) {
  const currentTab =
    seoData?.eSubType === 'cm'
      ? ENUM_CRICKET_MATCH_PAGES.c
      : ENUM_CRICKET_MATCH_PAGES[seoData.eSubType as string] || 'info'
  const errors: {
    matchDetail?: unknown
    headToHead?: unknown
  } = {}

  try {
    const matchDetailApi: Promise<GetMatchDetailResponse> = queryGraphql(MATCH_DETAIL, {
      input: { _id: seoData?.iId }
    })
    const subPagesApi: Promise<GetMatchSubPagesResponse> = unstable_cache(
      async () =>
        await queryGraphql(GET_SUB_PAGE_BY_SLUG_RAW, {
          input: { sSlug: seoData?.pathname }
        }),
      [seoData?.pathname as string],
      { revalidate: 20 }
    )()

    const [matchData, subPagesData, data] = await parallelCalls<
      [GetMatchDetailResponse, GetMatchSubPagesResponse, MatchDynamicTabData]
    >([matchDetailApi, subPagesApi, getTabData({ iMatchId: seoData.iId, currentTab })])

    if (matchData.error) {
      errors['matchDetail'] = matchData.error
    }

    const match = matchData?.data?.data?.getMatchById

    const subPages = subPagesData?.data?.data?.getSeosBySlug

    const Tabs = subPages ? getNavItems(subPages, currentTab, match?.oStatus?.sValue) : []

    let headToHeadData: GetHeadToHeadResponse | undefined

    if (
      currentTab === 'info' &&
      match?.oTeamScoreA?.oTeam?._id &&
      match?.oTeamScoreB?.oTeam?._id &&
      !matchData.error
    ) {
      try {
        headToHeadData = await unstable_cache(
          async () =>
            await queryGraphql(GET_HEAD_TO_HEAD, {
              input: {
                iTeamIds: [match?.oTeamScoreA?.oTeam?._id, match?.oTeamScoreB?.oTeam?._id]
              }
            }),
          [`${seoData.iId}1-${match?.oTeamScoreA?.oTeam?._id}-${match?.oTeamScoreB?.oTeam?._id}`],
          { revalidate: 120 }
        )()
      } catch (error) {
        console.log('Error from: getMatchData - headToHeadData', error)
        errors['headToHead'] = error
      }
    }

    return {
      Tabs,
      headToHeadData,
      currentTab,
      matchDetail: match,
      data: data?.data,
      errors
    }
  } catch (error) {
    console.log('Error from: getMatchData', error)
    throw error
  }
}

function getTabData({ iMatchId, currentTab }: { iMatchId: string; currentTab: string }) {
  switch (currentTab) {
    case 'info':
      return getOverviewData({ iMatchId })
    case 'scorecard':
      return getScorecardData({ iMatchId })
    case 'stats':
      return getLiveStatsData({ iMatchId })
    case 'squads':
      return getMatchSquadsData({ iMatchId })
    case 'odds':
      return getOddsData({ iMatchId })
    case 'summary':
      return getLiveAndSummaryData({ iMatchId })
    default:
      return null
  }
}

export async function getCommentaryData({ iMatchId, nLatestInningNumber }: CommonInputType) {
  try {
    const cQuery = combineQuery('commentaryQuery')
      .add(LIVE_INNING_DATA, { input: { iMatchId: iMatchId, nInningNumber: nLatestInningNumber } })
      .add(GET_COMMENTARY, { commentary: { iMatchId, nLimit: 15 } })
    const data = (await queryGraphql(cQuery.query, cQuery?.variables)).data
    return data
  } catch (error) {
    console.log('Error from: getCommentaryData', error)
    throw error
  }
}
export async function getOverviewData({ iMatchId }: Pick<CommonInputType, 'iMatchId'>) {
  try {
    const recentMatches: Promise<GetRecentStatsOfTeamResponse> = queryGraphql(
      GET_RECENT_MATCHES_STATS,
      {
        input: { iMatchId }
      }
    )
    const venueStats: Promise<GetVenueStatsByMatchIdResponse> = queryGraphql(GET_VENUE_STATS, {
      input: { iMatchId }
    })
    const matchOverview: Promise<MatchOverviewFrontResponse> = queryGraphql(GET_MATCH_OVERVIEW, {
      input: { iMatchId }
    })

    const [matchOverviewData, recentMatchesData, venueStatsData] = await parallelCalls<
      [MatchOverviewFrontResponse, GetRecentStatsOfTeamResponse, GetVenueStatsByMatchIdResponse]
    >([matchOverview, recentMatches, venueStats])

    const errors: MatchOverviewData['errors'] = {}
    if (matchOverviewData?.error) {
      errors['getMatchOverviewFront'] = matchOverviewData.error
    }
    if (recentMatchesData?.error) {
      errors['recentMatchesData'] = recentMatchesData.error
    }
    if (venueStatsData?.error) {
      errors['venueStatsData'] = venueStatsData.error
    }

    const data: MatchOverviewData = {
      type: 'overview',
      getMatchOverviewFront: matchOverviewData?.data?.data?.getMatchOverviewFront,
      recentMatchesData: recentMatchesData?.data,
      venueStatsData: venueStatsData?.data,
      errors
    }

    return data
  } catch (error) {
    console.log('Error from: getOverviewData', error)
    throw error
  }
}
export async function getScorecardData({ iMatchId }: Pick<CommonInputType, 'iMatchId'>) {
  try {
    const liveStats = queryGraphql(GET_MATCH_STATS, { input: { iMatchId } })
    const fullScoreBoard = queryGraphql(FULL_SCOREBOARD, { scorecard: { iMatchId } })
    const getMatchSquad = queryGraphql(GET_MATCH_SQUAD, { matchSquad: { iMatchId } })
    const [getMatchStatistics, fetchLiveInningsData, listMatchSquad] = await parallelCalls<
      [any, GetLiveInningsResponse, GetMatchSquadPlayerResponse]
    >([liveStats, fullScoreBoard, getMatchSquad])

    const errors: GetScorecardDataResponse['errors'] = {}
    if (getMatchStatistics?.error) {
      errors['getMatchStatistics'] = getMatchStatistics.error
    }
    if (fetchLiveInningsData?.error) {
      errors['fetchLiveInningsData'] = fetchLiveInningsData.error
    }
    if (listMatchSquad?.error) {
      errors['listMatchSquad'] = listMatchSquad.error
    }
    const data: GetScorecardDataResponse & { type: 'scorecard' } = {
      getMatchStatistics: getMatchStatistics?.data?.data?.getMatchStatistics,
      fetchLiveInningsData: fetchLiveInningsData?.data?.data?.fetchLiveInningsData,
      listMatchSquad: listMatchSquad?.data?.data?.listMatchSquad,
      type: 'scorecard'
    }

    return data
  } catch (error) {
    console.log('Error from: getScorecardData', error)
    throw error
  }
}
export async function getOversData({ iMatchId, nLatestInningNumber }: CommonInputType) {
  try {
    const data = (
      await queryGraphql(RECENT_OVER, {
        input: { iMatchId, nLimit: 5, nSkip: 0, nInningNumber: nLatestInningNumber }
      })
    ).data
    return data
  } catch (error) {
    console.log('Error from: getOversData', error)
    throw error
  }
}
export async function getLiveStatsData({ iMatchId }: Pick<CommonInputType, 'iMatchId'>) {
  try {
    const liveStats = queryGraphql(GET_MATCH_STATS, { input: { iMatchId } })
    const fullScoreBoard = queryGraphql(FULL_SCOREBOARD, { scorecard: { iMatchId } })
    const [getMatchStatistics, fetchLiveInningsData] = await parallelCalls<
      [any, GetLiveInningsResponse]
    >([liveStats, fullScoreBoard])

    const data: GetLiveStatsDataResponse & { type: 'stats' } = {
      liveStats: getMatchStatistics?.data?.data?.getMatchStatistics,
      inningData: fetchLiveInningsData?.data?.data.fetchLiveInningsData,
      type: 'stats'
    }

    return data
  } catch (error) {
    console.log('Error from: getLiveStatsData', error)
    throw error
  }
}
export async function getStandingData({ iSeriesId }: { iSeriesId: string }) {
  try {
    const cQuery = combineQuery('standingQuery')
      .add(GET_ROUNDS, { input: { iSeriesId } })
      .add(GET_STANDING_DATA, { standingInput: { iRoundId: null, iSeriesId } })
    const data = (await queryGraphql(cQuery.query, cQuery?.variables)).data
    return data
  } catch (error) {
    console.log('Error from: getStandingData', error)
    throw error
  }
}
export async function getMatchSquadsData({ iMatchId }: Pick<CommonInputType, 'iMatchId'>) {
  try {
    const data: GetMatchOverviewFrontResponse['data'] = (
      await queryGraphql(GET_MATCH_PLAYING_XI, { input: { iMatchId } })
    ).data
    return data
  } catch (error) {
    console.log('Error from: getMatchSquadsData', error)
    throw error
  }
}
export async function getOddsData({ iMatchId }: Pick<CommonInputType, 'iMatchId'>) {
  try {
    const data: GetOddsHistoryByMatchIdResponse['data'] = (
      await queryGraphql(GET_MATCH_ODDS, { input: { iMatchId } })
    ).data

    return data
  } catch (error) {
    console.log('Error from: getOddsData', error)
    throw error
  }
}

export async function getFantasyOverviewData({ iMatchId }: Pick<CommonInputType, 'iMatchId'>) {
  try {
    const data = (await queryGraphql(GET_FANTASY_OVERVIEW, { input: { iMatchId: iMatchId } })).data
      .getOverviewFront
    const fData = await queryGraphql(GET_FANTASY_ARTICLE_BY_ID, {
      input: { _id: data?.aCricPrediction[0]?._id }
    })
    return { overViewData: data, fantasyData: fData }
  } catch (error) {
    console.log('Error from: getFantasyOverviewData', error)
    throw error
  }
}

export async function getMatchesDataBySeries({
  aSeriesId,
  eStatus,
  nLimit
}: {
  aSeriesId?: string[]
  eStatus?: string
  nLimit?: number
}) {
  try {
    const data: UpcomingMatchCard[] = (
      await queryGraphql(GET_MATCHES_DATA, { input: { eStatus, aSeriesId, nLimit } })
    ).data.listAllFixturesMobileV2
    return data
  } catch (error) {
    console.log('Error from: getMatchSeoData', error)
  }
}

export async function getFeaturedSessionsData({
  iMatchId,
  nInningNumber
}: {
  iMatchId: string
  nInningNumber?: number
}) {
  try {
    const sessionData: GetFeaturedSessionsByMatchIdResponse['data']['getFeaturedSessionsByMatchId'] =
      (await queryGraphql(GET_FEATURED_SESSION_STATS, { input: { iMatchId, nInningNumber } })).data
        .getFeaturedSessionsByMatchId
    return { sessionData }
  } catch (error) {
    console.log('Error from: getFeaturedSessionsData', error)
    throw error
  }
}

export async function getNewsByMatchId({
  iMatchId,
  nLimit = 5
}: {
  iMatchId: string
  nLimit?: number
}) {
  try {
    const news: GetNewsByMatchIdResponse['data']['getNewsByMatchId'] = (
      await queryGraphql(GET_NEWS_DATA_BY_MATCH, { input: { iMatchId, nLimit } })
    ).data.getNewsByMatchId
    return news
  } catch (error) {
    console.log('Error from: getNewsByMatchId', error)
    throw error
  }
}

export async function getLiveAndSummaryData({ iMatchId }: { iMatchId: string }) {
  try {
    const fullScoreBoard = queryGraphql(FULL_SCOREBOARD, { scorecard: { iMatchId } })
    const session = queryGraphql(GET_FEATURED_SESSION_STATS, { input: { iMatchId } })
    const news = queryGraphql(GET_NEWS_DATA_BY_MATCH, { input: { iMatchId, nLimit: 5 } })

    const [fullScoreBoardData, sessionData, newsData] = await parallelCalls<
      [GetLiveInningsResponse, GetFeaturedSessionsByMatchIdResponse, GetNewsByMatchIdResponse]
    >([fullScoreBoard, session, news])

    const errors: MatchLiveSummaryData['errors'] = {}
    if (fullScoreBoardData?.error) {
      errors['fullScoreBoardData'] = fullScoreBoardData.error
    }
    if (sessionData?.error) {
      errors['sessionData'] = sessionData.error
    }
    if (newsData?.error) {
      errors['newsData'] = newsData.error
    }
    const data: MatchLiveSummaryData & { type: 'live' } = {
      fullScoreBoardData: fullScoreBoardData?.data?.data?.fetchLiveInningsData,
      sessionData: sessionData?.data?.data?.getFeaturedSessionsByMatchId,
      newsData: newsData?.data?.data?.getNewsByMatchId,
      type: 'live'
    }

    return data
  } catch (error) {
    console.log('Error from: getLiveAndSummaryData', error)
    throw error
  }
}
